import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { toast } from 'sonner';
import { 
  ArrowLeft, Car, Shield, FileText, CheckCircle, AlertTriangle, 
  Calculator, Printer, DollarSign, Calendar, ChevronRight, Lock,
  Building, Users, Landmark, RefreshCw, Info, Scale, Pen, Mail, Send, Package, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  generateAutoSubmissionMemorandum, 
  generateAutoAcceptanceForm,
  generateTokenizedCheckDisclosure
} from '../utils/documentTemplates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BETH_TOKEN = "0x6822824c5349bf005e353ec5dda3fa34595ea811";

// Treasury-Indexed Auto Obligation Products for REFINANCING
const TIAO_PRODUCTS = [
  {
    id: 'tiao-36',
    name: 'TIAO-36 (3-Year)',
    description: '36-month Treasury-indexed auto obligation',
    term: 36,
    referenceRate: 'U.S. Treasury 2-Year CMR',
    baseAPR: 4.99,
    spread: 2.00,
    minAmount: 5000,
    maxAmount: 75000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Fastest Payoff']
  },
  {
    id: 'tiao-48',
    name: 'TIAO-48 (4-Year)',
    description: '48-month Treasury-indexed auto obligation',
    term: 48,
    referenceRate: 'U.S. Treasury 3-Year CMR',
    baseAPR: 5.49,
    spread: 2.25,
    minAmount: 5000,
    maxAmount: 100000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Balanced Term']
  },
  {
    id: 'tiao-60',
    name: 'TIAO-60 (5-Year)',
    description: '60-month Treasury-indexed auto obligation',
    term: 60,
    referenceRate: 'U.S. Treasury 5-Year CMR',
    baseAPR: 5.99,
    spread: 2.50,
    minAmount: 5000,
    maxAmount: 150000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Lower Monthly']
  },
  {
    id: 'tiao-72',
    name: 'TIAO-72 (6-Year)',
    description: '72-month Treasury-indexed auto obligation',
    term: 72,
    referenceRate: 'U.S. Treasury 5-Year CMR',
    baseAPR: 6.49,
    spread: 2.75,
    minAmount: 5000,
    maxAmount: 200000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Lowest Monthly']
  }
];

const AutoRefinance = () => {
  const { user, token } = useAuth();
  const { account, bethBalance } = useWeb3();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [costSheet, setCostSheet] = useState(null);
  const [novations, setNovations] = useState([]);
  const [showNovationForm, setShowNovationForm] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [selectedNovation, setSelectedNovation] = useState(null);
  const [lenderEmail, setLenderEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Form for borrower info
  const [formData, setFormData] = useState({
    borrower_name: '',
    borrower_wallet: ''
  });
  
  // Novation form for auto refinancing
  const [novationForm, setNovationForm] = useState({
    original_creditor_name: '',
    original_creditor_contact: '',
    original_account_number: '',
    original_principal: '',
    original_rate: '',
    original_monthly_payment: '',
    original_remaining_balance: '',
    original_maturity_date: '',
    vehicle_vin: '',
    vehicle_description: '',
    vehicle_value: '',
    payoff_amount: '',
    effective_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchNovations();
  }, [token]);

  useEffect(() => {
    if (account) {
      setFormData(prev => ({ ...prev, borrower_wallet: account }));
    }
  }, [account]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({ ...prev, borrower_name: user.name || user.email }));
    }
  }, [user]);

  const fetchNovations = async () => {
    try {
      const res = await fetch(`${API}/auto-financing/novations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNovations(data.agreements || []);
      }
    } catch (error) {
      console.error('Error fetching novations:', error);
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    if (novationForm.payoff_amount) {
      calculateCostSheet(product, parseFloat(novationForm.payoff_amount));
    }
  };

  const calculateCostSheet = (product, principal) => {
    if (!product || !principal || principal < product.minAmount) {
      setCostSheet(null);
      return;
    }
    
    const indexedAPR = product.baseAPR;
    const monthlyRate = indexedAPR / 100 / 12;
    const numPayments = product.term;
    
    const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    const totalPayments = monthlyPayment * numPayments;
    const totalIndexAdjustment = totalPayments - principal;
    
    // Platform fees
    const structuringFee = principal * 0.02;
    const pasEnhancementFee = principal * 0.01;
    const totalFees = structuringFee + pasEnhancementFee;
    
    // BETH collateral requirement (10% of principal at $1.00/BETH)
    const bethRequired = Math.ceil(principal * 0.10);
    
    setCostSheet({
      principal: principal.toFixed(2),
      indexedAPR: indexedAPR.toFixed(2),
      term: product.term,
      monthlyPayment: monthlyPayment.toFixed(2),
      totalPayments: totalPayments.toFixed(2),
      totalIndexAdjustment: totalIndexAdjustment.toFixed(2),
      structuringFee: structuringFee.toFixed(2),
      pasEnhancementFee: pasEnhancementFee.toFixed(2),
      totalFees: totalFees.toFixed(2),
      bethCollateral: bethRequired,
      collateralRatio: 10
    });
  };

  const createNovationAgreement = async () => {
    if (!selectedProduct || !costSheet || !novationForm.original_creditor_name) {
      toast.error('Please fill in original creditor details and select a TIAO product');
      return;
    }
    
    if (!novationForm.vehicle_vin || novationForm.vehicle_vin.length < 17) {
      toast.error('Please enter a valid 17-character VIN');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/auto-financing/novation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...novationForm,
          borrower_name: formData.borrower_name,
          borrower_wallet: formData.borrower_wallet,
          new_principal: parseFloat(costSheet.principal),
          new_indexed_apr: parseFloat(costSheet.indexedAPR),
          new_term_months: selectedProduct.term,
          new_monthly_payment: parseFloat(costSheet.monthlyPayment),
          treasury_index: selectedProduct.referenceRate,
          beth_collateral: parseFloat(costSheet.bethCollateral)
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Auto Loan Novation Agreement created! Awaiting creditor consent.');
        fetchNovations();
        setShowNovationForm(false);
        // Reset form
        setNovationForm({
          original_creditor_name: '',
          original_creditor_contact: '',
          original_account_number: '',
          original_principal: '',
          original_rate: '',
          original_monthly_payment: '',
          original_remaining_balance: '',
          original_maturity_date: '',
          vehicle_vin: '',
          vehicle_description: '',
          vehicle_value: '',
          payoff_amount: '',
          effective_date: new Date().toISOString().split('T')[0]
        });
        setCostSheet(null);
        setSelectedProduct(null);
      } else {
        toast.error(data.error || 'Failed to create novation agreement');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error creating novation agreement');
    } finally {
      setLoading(false);
    }
  };

  const sendConsentRequest = async (novation) => {
    if (!lenderEmail) {
      toast.error('Please enter the creditor email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      const res = await fetch(`${API}/auto-financing/novation/${novation.agreement_id}/send-consent-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ lender_email: lenderEmail })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(`Consent request email sent to ${lenderEmail}`);
        setShowConsentModal(false);
        setLenderEmail('');
        setSelectedNovation(null);
        fetchNovations();
      } else {
        toast.error(data.error || 'Failed to send consent request');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error sending consent request');
    } finally {
      setSendingEmail(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_lender_consent': return 'bg-orange-500/20 text-orange-400';
      case 'pending_signatures': return 'bg-yellow-500/20 text-yellow-400';
      case 'fully_executed': return 'bg-green-500/20 text-green-400';
      case 'rejected': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending_lender_consent': return 'Awaiting Creditor Consent';
      case 'pending_signatures': return 'Awaiting Signatures';
      case 'fully_executed': return 'Fully Executed';
      case 'rejected': return 'Rejected';
      default: return status?.replace(/_/g, ' ').toUpperCase() || 'Unknown';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="auto-refinance-page">
      {/* Header */}
      <header className="bg-[#0d1d35] border-b border-[#1a2d4a]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <RefreshCw className="w-6 h-6 text-purple-400" />
                  Auto Loan Refinance
                </h1>
                <p className="text-sm text-gray-400">Novate existing auto loans via Treasury-Indexed obligation</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/new-auto-financing">
                <Button variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10">
                  <Plus className="w-4 h-4 mr-2" />
                  New Vehicle Purchase
                </Button>
              </Link>
              <Button 
                onClick={() => setShowNovationForm(true)}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="new-refinance-btn"
              >
                <Scale className="w-4 h-4 mr-2" />
                Start Refinance
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Disclaimer Banner */}
      <div className="bg-gradient-to-r from-purple-900/30 to-purple-800/30 border-b border-purple-700/50">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center gap-3 text-purple-200 text-xs">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <p>
              <strong>REFINANCE PROCESS:</strong> This creates a NOVATION agreement to replace your existing auto loan. 
              Original creditor consent is REQUIRED before execution.
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* New Novation Form Modal */}
        {showNovationForm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className="max-w-4xl w-full bg-[#0d1d35] border-[#1a2d4a] my-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Scale className="w-5 h-5 text-purple-400" />
                  Create Auto Loan Novation Agreement
                </CardTitle>
                <CardDescription>
                  Refinance your existing auto loan with a Treasury-indexed obligation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Original Loan Details */}
                <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                  <h3 className="text-sm font-medium text-purple-400 mb-4">Original Loan Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-400">Original Creditor/Lender</label>
                      <Input
                        value={novationForm.original_creditor_name}
                        onChange={(e) => setNovationForm(prev => ({ ...prev, original_creditor_name: e.target.value }))}
                        placeholder="Bank or Finance Company Name"
                        className="mt-1 bg-[#0d1d35] border-[#1a2d4a] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Creditor Contact</label>
                      <Input
                        value={novationForm.original_creditor_contact}
                        onChange={(e) => setNovationForm(prev => ({ ...prev, original_creditor_contact: e.target.value }))}
                        placeholder="Phone or Email"
                        className="mt-1 bg-[#0d1d35] border-[#1a2d4a] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Account/Loan Number</label>
                      <Input
                        value={novationForm.original_account_number}
                        onChange={(e) => setNovationForm(prev => ({ ...prev, original_account_number: e.target.value }))}
                        placeholder="Account Number"
                        className="mt-1 bg-[#0d1d35] border-[#1a2d4a] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Payoff Amount</label>
                      <Input
                        type="number"
                        value={novationForm.payoff_amount}
                        onChange={(e) => {
                          setNovationForm(prev => ({ ...prev, payoff_amount: e.target.value }));
                          if (selectedProduct && e.target.value) {
                            calculateCostSheet(selectedProduct, parseFloat(e.target.value));
                          }
                        }}
                        placeholder="Current Payoff Amount"
                        className="mt-1 bg-[#0d1d35] border-[#1a2d4a] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Current Interest Rate (%)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={novationForm.original_rate}
                        onChange={(e) => setNovationForm(prev => ({ ...prev, original_rate: e.target.value }))}
                        placeholder="Current APR"
                        className="mt-1 bg-[#0d1d35] border-[#1a2d4a] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Current Monthly Payment</label>
                      <Input
                        type="number"
                        value={novationForm.original_monthly_payment}
                        onChange={(e) => setNovationForm(prev => ({ ...prev, original_monthly_payment: e.target.value }))}
                        placeholder="Monthly Payment"
                        className="mt-1 bg-[#0d1d35] border-[#1a2d4a] text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Vehicle Information */}
                <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                  <h3 className="text-sm font-medium text-purple-400 mb-4">Vehicle Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-400">Vehicle VIN</label>
                      <Input
                        value={novationForm.vehicle_vin}
                        onChange={(e) => setNovationForm(prev => ({ ...prev, vehicle_vin: e.target.value.toUpperCase() }))}
                        placeholder="17-character VIN"
                        maxLength={17}
                        className="mt-1 bg-[#0d1d35] border-[#1a2d4a] text-white font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Estimated Vehicle Value</label>
                      <Input
                        type="number"
                        value={novationForm.vehicle_value}
                        onChange={(e) => setNovationForm(prev => ({ ...prev, vehicle_value: e.target.value }))}
                        placeholder="Current Market Value"
                        className="mt-1 bg-[#0d1d35] border-[#1a2d4a] text-white"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-gray-400">Vehicle Description</label>
                      <Input
                        value={novationForm.vehicle_description}
                        onChange={(e) => setNovationForm(prev => ({ ...prev, vehicle_description: e.target.value }))}
                        placeholder="Year, Make, Model, Trim"
                        className="mt-1 bg-[#0d1d35] border-[#1a2d4a] text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* New Loan Terms */}
                <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                  <h3 className="text-sm font-medium text-purple-400 mb-4">New TIAO Terms</h3>
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {TIAO_PRODUCTS.map((product) => (
                      <div
                        key={product.id}
                        onClick={() => handleProductSelect(product)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all text-center ${
                          selectedProduct?.id === product.id 
                            ? 'border-purple-500 bg-purple-500/10' 
                            : 'border-[#1a2d4a] hover:border-purple-500/50'
                        }`}
                      >
                        <div className="text-lg font-bold text-white">{product.term}mo</div>
                        <div className="text-sm text-purple-400">{product.baseAPR}% APR</div>
                      </div>
                    ))}
                  </div>
                  
                  {costSheet && (
                    <div className="mt-4 p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-xs text-gray-400">New Monthly Payment</div>
                          <div className="text-xl font-bold text-white">${parseFloat(costSheet.monthlyPayment).toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">New APR</div>
                          <div className="text-xl font-bold text-purple-400">{costSheet.indexedAPR}%</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">BETH Required</div>
                          <div className="text-xl font-bold text-white">{costSheet.bethCollateral.toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setShowNovationForm(false)} className="border-[#1a2d4a]">
                    Cancel
                  </Button>
                  <Button 
                    onClick={createNovationAgreement}
                    disabled={loading || !selectedProduct || !novationForm.original_creditor_name || !novationForm.vehicle_vin}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {loading ? 'Creating...' : 'Create Novation Agreement'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Consent Request Modal */}
        {showConsentModal && selectedNovation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-orange-400" />
                  Send Creditor Consent Request
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-[#0a1628] rounded-lg">
                  <div className="text-xs text-gray-400">Agreement</div>
                  <div className="text-white font-mono text-sm">{selectedNovation.agreement_id}</div>
                </div>
                <div>
                  <label className="text-xs text-gray-400">Creditor Email Address</label>
                  <Input
                    type="email"
                    value={lenderEmail}
                    onChange={(e) => setLenderEmail(e.target.value)}
                    placeholder="creditor@bank.com"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setShowConsentModal(false); setSelectedNovation(null); }} className="flex-1 border-[#1a2d4a]">
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => sendConsentRequest(selectedNovation)}
                    disabled={sendingEmail || !lenderEmail}
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                  >
                    {sendingEmail ? 'Sending...' : 'Send Request'}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Existing Novations List */}
        <div className="space-y-6">
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Scale className="w-5 h-5 text-purple-400" />
                My Refinance Agreements ({novations.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {novations.length > 0 ? (
                <div className="space-y-4">
                  {novations.map((novation) => (
                    <div key={novation.agreement_id} className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="font-mono text-sm text-gray-400">{novation.agreement_id}</div>
                          <div className="text-white font-medium mt-1">{novation.vehicle_description || 'Vehicle'}</div>
                          <div className="text-xs text-gray-500">VIN: {novation.vehicle_vin}</div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(novation.status)}`}>
                          {getStatusLabel(novation.status)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-4 mb-4">
                        <div>
                          <div className="text-xs text-gray-500">Payoff Amount</div>
                          <div className="text-white">${parseFloat(novation.payoff_amount || novation.new_principal || 0).toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">New Rate</div>
                          <div className="text-purple-400">{novation.new_indexed_apr}%</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">New Monthly</div>
                          <div className="text-white">${parseFloat(novation.new_monthly_payment || 0).toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">BETH Collateral</div>
                          <div className="text-white">{parseFloat(novation.beth_collateral || 0).toLocaleString()}</div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" className="border-[#1a2d4a] text-gray-400">
                          <Printer className="w-4 h-4 mr-1" /> Print
                        </Button>
                        {novation.status === 'pending_lender_consent' && (
                          <Button 
                            size="sm" 
                            className="bg-orange-600 hover:bg-orange-700"
                            onClick={() => { setSelectedNovation(novation); setShowConsentModal(true); }}
                          >
                            <Send className="w-4 h-4 mr-1" /> Request Consent
                          </Button>
                        )}
                        {novation.status === 'pending_signatures' && (
                          <Button 
                            size="sm" 
                            className="bg-purple-600 hover:bg-purple-700"
                            onClick={() => {
                              toast.info('E-Signature workflow: Document ready for signing. In production, this would open DocuSign or similar e-signature service.');
                              setTimeout(() => toast.success('Signature captured! Document marked as signed.'), 1500);
                            }}
                          >
                            <Pen className="w-4 h-4 mr-1" /> Sign
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Scale className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No refinance agreements yet</p>
                  <p className="text-sm mt-2">Click &quot;Start Refinance&quot; to refinance an existing auto loan</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AutoRefinance;
